/**
 * Fake cart — cart_conditional_items free-gift sync
 * ==================================================
 *
 * Reads rules from #FakeCartConditionalItemsConfig (metaobject list on the
 * Fake Cart section) and keeps free-gift lines in sync with the fake cart.
 *
 * Rule shape (per metaobject entry):
 *   apply_to_all   → when true, any paid cart line triggers the gift (buy list ignored)
 *   customer_buys  → product ids; ANY one present in cart triggers the gifts
 *   customer_gets  → list of gift products (each serialized with variant id + display fields)
 *   how_many       → fixed quantity for EACH gift in the list (does NOT scale with cart qty)
 *   hide_in_cart   → omit this rule's gifts from the drawer; they still sync and check out
 *
 * Hide in drawer (still in cart / checkout):
 *   - Section setting hide_conditional_gifts_in_cart (data-hide-all-in-cart), or
 *   - Per-rule metaobject field hide_in_cart
 *
 * Behaviour:
 *   1. Evaluate which gifts should be present (by gift variant id).
 *   2. If multiple rules award the same gift, it is added ONLY ONCE
 *      (how_many = max among matching rules).
 *   3. Add missing gifts / fix quantity / remove gifts whose triggers left.
 *
 * Free-gift lines are marked with:
 *   properties._is_free_gift = "true"
 *   properties._source       = "cart_conditional_items"
 *
 * Sync is idempotent and re-entrancy guarded so add/remove does not loop
 * on fake-cart:updated.
 */
(function () {
  "use strict";

  if (!window.FakeCart || window.FakeCartConditionalItems) return;

  var SOURCE = "cart_conditional_items";
  var CONFIG_ID = "FakeCartConditionalItemsConfig";
  var PROP_IS_GIFT = "_is_free_gift";
  var PROP_SOURCE = "_source";
  var PROP_RULE = "_conditional_rule";

  var cachedRules = null;
  var cachedHideAll = null;
  var syncing = false;

  /* ------------------------------------------------------------------ */
  /* Config                                                              */
  /* ------------------------------------------------------------------ */

  function getConfigEl() {
    return document.getElementById(CONFIG_ID);
  }

  /**
   * Section-level: hide every conditional gift line in the drawer.
   * @returns {boolean}
   */
  function hideAllInCart() {
    if (cachedHideAll !== null) return cachedHideAll;
    var el = getConfigEl();
    cachedHideAll = Boolean(el && el.getAttribute("data-hide-all-in-cart") === "true");
    return cachedHideAll;
  }

  /**
   * Parse the Liquid-emitted JSON config once.
   * @returns {object[]}
   */
  function getRules() {
    if (cachedRules !== null) return cachedRules;

    var el = getConfigEl();
    if (!el) {
      cachedRules = [];
      return cachedRules;
    }

    try {
      var parsed = JSON.parse(el.textContent) || [];
      cachedRules = Array.isArray(parsed) ? parsed.filter(isValidRule) : [];
    } catch (error) {
      console.warn("[fake-cart-conditional-items] invalid config JSON", error);
      cachedRules = [];
    }

    return cachedRules;
  }

  /**
   * customer_gets is a product list. A single object is still accepted.
   * @param {object} rule
   * @returns {object[]}
   */
  function giftList(rule) {
    var gets = rule && rule.customer_gets;
    if (Array.isArray(gets)) {
      return gets.filter(function (gift) {
        return gift && gift.id;
      });
    }
    if (gets && gets.id) return [gets];
    return [];
  }

  function isValidRule(rule) {
    if (!giftList(rule).length) return false;
    if (rule.apply_to_all) return true;
    return Array.isArray(rule.customer_buys_product_ids) && rule.customer_buys_product_ids.length > 0;
  }

  /* ------------------------------------------------------------------ */
  /* Line helpers                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * True when a fake-cart line is a free gift managed by this module.
   * @param {object} item
   * @returns {boolean}
   */
  function isManagedFreeGift(item) {
    if (!item || !item.properties) return false;
    return (
      String(item.properties[PROP_IS_GIFT]) === "true" &&
      String(item.properties[PROP_SOURCE] || "") === SOURCE
    );
  }

  /**
   * True when a fake-cart line is any free gift (managed or otherwise).
   * Exposed for fake-cart.js UI / pricing.
   * @param {object} item
   * @returns {boolean}
   */
  function isFreeGiftLine(item) {
    if (!item || !item.properties) return false;
    return String(item.properties[PROP_IS_GIFT]) === "true";
  }

  /**
   * True when a managed free gift should be omitted from the fake cart drawer.
   * The line stays in FakeCart storage and is still sent at checkout.
   * A gift is hidden when the section setting is on, or when any rule that
   * awards that variant has hide_in_cart.
   * @param {object} item
   * @returns {boolean}
   */
  function isHiddenInCart(item) {
    if (!isManagedFreeGift(item)) return false;
    if (hideAllInCart()) return true;

    var variantId = String(item.id);
    return getRules().some(function (rule) {
      if (!rule.hide_in_cart) return false;
      return giftList(rule).some(function (gift) {
        return String(gift.id) === variantId;
      });
    });
  }

  /**
   * Build the product-id set of non-gift lines currently in the fake cart.
   * Trigger matching ignores free-gift lines so a gift never unlocks itself.
   * @param {object[]} items
   * @returns {Set<string>}
   */
  function collectPaidProductIds(items) {
    var ids = new Set();
    (items || []).forEach(function (item) {
      if (isFreeGiftLine(item)) return;
      if (item.product_id != null && item.product_id !== "") {
        ids.add(String(item.product_id));
      }
    });
    return ids;
  }

  /**
   * Does this rule apply to the current paid lines?
   * apply_to_all matches any non-gift line. Otherwise any product from the buy list is enough.
   * Quantity does not matter. An empty paid set never matches, so gifts cannot unlock themselves.
   * @param {object} rule
   * @param {Set<string>} paidProductIds
   * @returns {boolean}
   */
  function ruleMatches(rule, paidProductIds) {
    if (!paidProductIds.size) return false;
    if (rule.apply_to_all) return true;
    return rule.customer_buys_product_ids.some(function (productId) {
      return paidProductIds.has(String(productId));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Desired-state resolution                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Compute the gifts that should be in the cart right now.
   * Dedupes by gift variant id so two rules awarding the same sticker
   * only produce one line (how_many = max of matching rules).
   *
   * @param {object[]} rules
   * @param {object[]} items - current fake cart lines
   * @returns {Map<string, { variantId: number, howMany: number, gift: object, ruleName: string }>}
   */
  function resolveDesiredGifts(rules, items) {
    var paidProductIds = collectPaidProductIds(items);
    var desired = new Map();

    rules.forEach(function (rule) {
      if (!ruleMatches(rule, paidProductIds)) return;

      var howMany = Math.max(1, parseInt(rule.how_many, 10) || 1);
      giftList(rule).forEach(function (gift) {
        var variantId = String(gift.id);
        var existing = desired.get(variantId);

        if (!existing) {
          desired.set(variantId, {
            variantId: gift.id,
            howMany: howMany,
            gift: gift,
            ruleName: rule.name || rule.handle || SOURCE,
          });
          return;
        }

        if (howMany > existing.howMany) {
          existing.howMany = howMany;
        }
      });
    });

    return desired;
  }

  /**
   * Build a FakeCart.addItem payload for a free gift.
   * price is forced to 0; catalog price kept on compare_at_price for display.
   * @param {object} desired
   * @returns {object}
   */
  function buildGiftLineInput(desired) {
    var gift = desired.gift || {};
    return {
      id: desired.variantId,
      product_id: gift.product_id || null,
      quantity: desired.howMany,
      title: gift.title || "",
      variant_title: gift.variant_title || "",
      price: 0,
      compare_at_price: gift.compare_at_price || 0,
      image: gift.image || "",
      url: gift.url || "",
      handle: gift.handle || "",
      properties: {
        [PROP_IS_GIFT]: "true",
        [PROP_SOURCE]: SOURCE,
        [PROP_RULE]: desired.ruleName || SOURCE,
      },
    };
  }

  /* ------------------------------------------------------------------ */
  /* Sync                                                                */
  /* ------------------------------------------------------------------ */

  /**
   * Reconcile managed free-gift lines with the current cart.
   *
   * Steps:
   *   A. Resolve desired gifts from matching rules (deduped by variant).
   *   B. Remove managed gift lines that are no longer desired.
   *   C. Add missing desired gifts / correct quantity on existing ones.
   *
   * Wrapped in `syncing` so the fake-cart:updated events we fire do not
   * re-enter this function.
   */
  function syncConditionalFreeGifts() {
    if (syncing || !window.FakeCart) return;

    var rules = getRules();
    if (!rules.length) {
      // Still prune any leftover managed gifts if config was cleared.
      removeAllManagedGifts();
      return;
    }

    syncing = true;
    try {
      var items = window.FakeCart.getItems();
      var desired = resolveDesiredGifts(rules, items);
      var managed = items.filter(isManagedFreeGift);

      // B — remove gifts whose triggers are gone (or whose variant is no longer awarded)
      var keysToRemove = [];
      managed.forEach(function (line) {
        if (!desired.has(String(line.id))) {
          keysToRemove.push(line.key);
        }
      });
      if (keysToRemove.length) {
        window.FakeCart.removeLines(keysToRemove);
        items = window.FakeCart.getItems();
        managed = items.filter(isManagedFreeGift);
      }

      // C — add / fix qty for each desired gift
      desired.forEach(function (entry) {
        var existing = managed.find(function (line) {
          return String(line.id) === String(entry.variantId);
        });

        if (!existing) {
          window.FakeCart.addItem(buildGiftLineInput(entry));
          return;
        }

        if (existing.quantity !== entry.howMany) {
          window.FakeCart.setLineQuantity(existing.key, entry.howMany);
        }
      });
    } finally {
      syncing = false;
    }
  }

  /**
   * Drop every gift line owned by this module (config empty / disabled).
   */
  function removeAllManagedGifts() {
    if (syncing || !window.FakeCart) return;
    var keys = window.FakeCart.getItems().filter(isManagedFreeGift).map(function (line) {
      return line.key;
    });
    if (!keys.length) return;

    syncing = true;
    try {
      window.FakeCart.removeLines(keys);
    } finally {
      syncing = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function onCartUpdated() {
    if (syncing) return;
    syncConditionalFreeGifts();
  }

  function boot() {
    syncConditionalFreeGifts();
  }

  document.addEventListener("fake-cart:updated", onCartUpdated);
  window.addEventListener("fake-cart:updated", onCartUpdated);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.FakeCartConditionalItems = {
    sync: syncConditionalFreeGifts,
    getRules: getRules,
    isFreeGiftLine: isFreeGiftLine,
    isManagedFreeGift: isManagedFreeGift,
    isHiddenInCart: isHiddenInCart,
    hideAllInCart: hideAllInCart,
    resolveDesiredGifts: resolveDesiredGifts,
  };
})();
 